import React from "react";
import PropTypes from "prop-types";
import { Title } from "rbx";
import { Checkbox } from "@mui/material";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { DetailedDataTable } from "../../components";
import FilterComponent from "../../components/DetailedDataTable/SearchFiltersModal";
import { GET_EVALUATIONS_PPA_ASSIGNMENTS } from "../AllEvaluations/graphql";
import { SEND_PPA_REMINDER_EMAILS } from "./graphql/ppaAssignments.mutations";
import formatDate from "../../utils/formatDate";
import checkMobile from "../../utils/checkForMobile";
import { useAuth } from "../../context";

const PPAAssignmentsAction = ({
  executeQuery,
  isSomeRowsSelected,
  onClearRowSelection,
  rowSelection,
}) => {
  const evaluationIds = Object.keys(rowSelection);
  const handleCompleted = () => {
    toast.success("Reminder emails sent.");
    onClearRowSelection();
    if (typeof executeQuery === "function") executeQuery();
  };
  const handleError = () => {
    toast.error("Failed to send reminder emails.");
  };
  const [sendPPAReminderEmails, { loading }] = useMutation(SEND_PPA_REMINDER_EMAILS, {
    onCompleted: handleCompleted,
    onError: handleError,
  });

  const handleSendEmail = () => {
    if (evaluationIds.length === 0) return;
    sendPPAReminderEmails({ variables: { evaluationIds } });
  };

  return (
    <button
      type="button"
      onClick={handleSendEmail}
      disabled={loading || !isSomeRowsSelected}
      style={{
        background: "#052e6f",
        color: "#fff",
        border: "1px solid #052e6f",
        borderRadius: "4px",
        padding: "0.5rem 1rem",
        cursor: loading || !isSomeRowsSelected ? "not-allowed" : "pointer",
        fontWeight: 600,
        opacity: isSomeRowsSelected ? 1 : 0.6,
      }}
    >
      {loading ? "Sending…" : "Send Email"}
    </button>
  );
};

PPAAssignmentsAction.propTypes = {
  executeQuery: PropTypes.func,
  isSomeRowsSelected: PropTypes.bool,
  onClearRowSelection: PropTypes.func.isRequired,
  rowSelection: PropTypes.object.isRequired,
};

PPAAssignmentsAction.defaultProps = {
  executeQuery: undefined,
  isSomeRowsSelected: false,
};

const COLUMNS = [
  {
    id: "select",
    header: "",
    enableSorting: false,
    cell: ({ row }) => {
      if (row.original?.status !== "INCOMPLETE") return null;
      return (
        <Checkbox
          size="small"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          onClick={(e) => e.stopPropagation()}
        />
      );
    },
  },
  { header: "Status", id: "status", accessorKey: "status" },
  { header: "Subject", id: "owner", accessorKey: "owner" },
  { header: "Subject Employee Number", id: "ownerEmployeeNumber", accessorKey: "ownerEmployeeNumber" },
  { header: "Assigned PPA", id: "formAuthor", accessorKey: "formAuthor" },
  { header: "Assigned PPA Employee Number", id: "formAuthorEmployeeNumber", accessorKey: "formAuthorEmployeeNumber" },
  { header: "Manager", id: "manager", accessorKey: "manager" },
  {
    header: "Due Date",
    id: "dateToCompleteBy",
    accessorFn: (row) => row?.dateToCompleteBy && formatDate(row?.dateToCompleteBy),
  },
  {
    header: "Date Completed",
    id: "dateCompleted",
    accessorFn: (row) => row?.dateCompleted && formatDate(row?.dateCompleted),
  },
];

const PPAAssignments = () => {
  const isMobile = checkMobile();
  const {
    state: { user },
  } = useAuth();
  const managerEmpNo = user?.employeeNumber?.trim?.();
  // Grant full PPA visibility to select employee numbers.
  const specialPPAAccess = ["11888"];
  const hasAllPPAAccess = specialPPAAccess.includes(managerEmpNo);
  const managerEmployeeNumberFilter =
    !hasAllPPAAccess && managerEmpNo ? { contains: managerEmpNo } : undefined;
  return (
    <div className={isMobile ? "workshop-container-mobile" : "workshop-container"}>
      <div className="workshop">
        <Title>PPA Assignments</Title>
        <DetailedDataTable
          aggregateName="aggregateEvaluation"
          columns={COLUMNS}
          dataExtractor={(data) => data?.evaluations}
          defaultSorting={[{ id: "owner", desc: false }]}
          enableRowSelection={(row) => row.original?.status === "INCOMPLETE"}
          FilterComponent={FilterComponent}
          location="ppa-assignments"
          name="ppa-assignments"
          query={GET_EVALUATIONS_PPA_ASSIGNMENTS}
          totalExtractor={(total) => total}
          ActionComponent={PPAAssignmentsAction}
          where={{
            managerEmployeeNumber: managerEmployeeNumberFilter,
            OR: [
              { title: { contains: "PPA" } },
              { title: { contains: "Internal Client" } },
            ],
            review: {
              is: {
                status: {
                  notIn: ["COMPLETED"],
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default PPAAssignments;


